import React, { useState, useEffect } from 'react';
import api, { logisticsAPI } from '../api';
import { extractErrorMessage, withRetry } from '../utils/errorHandling';
import LoadingSpinner from './LoadingSpinner';
import ErrorDisplay from './ErrorDisplay';
import CollapsibleFilters from './CollapsibleFilters';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import type { 
  ShipmentListItem, 
  ShipmentListResponse, 
  ShipmentSearchParams,
} from '../types/logistics';

interface LogisticsDashboardProps {
  onCreateShipment?: () => void;
  onShipmentSelect?: (awb: string) => void;
  userRole?: string;
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending', booked: 'Booked', picked_up: 'Picked Up',
  in_transit: 'In Transit', out_for_delivery: 'Out for Delivery',
  delivered: 'Delivered', cancelled: 'Cancelled', returned: 'Returned', lost: 'Lost',
};

const PROVIDER_LABELS: Record<string, string> = {
  bluedart: 'BlueDart', delhivery: 'Delhivery', custom: 'Custom', 'hand-delivery': 'Hand-Delivery',
};

interface LogisticsOrder {
  id: number;
  order_number: string;
  external_order_id: string | null;
  customer_name: string | null;
  order_date: string;
  status: string;
  total_amount: number;
  channel: string | null;
  dc_number: string | null;
  dc_id: number | null;
  dc_date: string | null;
  awb_number: string | null;
  shipping_status: string | null;
  ship_date: string | null;
  shipping_provider: string | null;
}

const LogisticsDashboard: React.FC<LogisticsDashboardProps> = ({ 
  onCreateShipment, 
  onShipmentSelect,
  userRole = 'viewer'
}) => {
  // Orders state
  const [orders, setOrders] = useState<LogisticsOrder[]>([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelConfirm, setCancelConfirm] = useState<{show: boolean; id: number | null; name: string}>({show: false, id: null, name: ''});

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterOrderStatus, setFilterOrderStatus] = useState('');
  const [filterChannel, setFilterChannel] = useState('');
  const [filterProvider, setFilterProvider] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // Selection & bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<any>(null);

  // Sorting
  const [sortField, setSortField] = useState<string>('order_date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Detail view
  const [selectedOrder, setSelectedOrder] = useState<LogisticsOrder | null>(null);

  // DC modal
  const [showDCModal, setShowDCModal] = useState(false);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const [dcForm, setDcForm] = useState({ warehouse_id: 0, vehicle_number: '', transport_mode: 'courier' });

  // Import AWB state
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);

  // Track AWB state
  const [trackAwb, setTrackAwb] = useState('');
  const [trackCarrier, setTrackCarrier] = useState('bluedart');
  const [tracking, setTracking] = useState(false);
  const [trackResult, setTrackResult] = useState<any>(null);
  const [trackError, setTrackError] = useState('');

  // Pincode check state
  const [pincodeCheck, setPincodeCheck] = useState('');
  const [pincodeCarrier, setPincodeCarrier] = useState('delhivery');
  const [pincodeChecking, setPincodeChecking] = useState(false);
  const [pincodeResult, setPincodeResult] = useState<any>(null);

  // Bulk serviceability check state
  const [serviceabilityChecking, setServiceabilityChecking] = useState(false);
  const [serviceabilityResult, setServiceabilityResult] = useState<any>(null);
  const [serviceabilityCarrier, setServiceabilityCarrier] = useState('all');

  // Pickup scheduling state
  const [showPickupModal, setShowPickupModal] = useState(false);
  const [pickupScheduling, setPickupScheduling] = useState(false);
  const [pickupResult, setPickupResult] = useState<any>(null);
  const [pickupForm, setPickupForm] = useState({ date: '', time: '14:00', carrier: 'delhivery' });
  const [pendingPickupCount, setPendingPickupCount] = useState<any>(null);

  // Provider booking state
  const [bookingProvider, setBookingProvider] = useState('delhivery');
  const [bookingShippingMode, setBookingShippingMode] = useState('Surface');

  useEffect(() => { fetchOrders(); }, []);

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {};
      if (searchQuery) params.q = searchQuery;
      if (filterStatus) params.shipping_status = filterStatus;
      if (filterProvider) params.shipping_provider = filterProvider;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      const res = await api.get('/logistics/orders', { params: { ...params, limit: 500 } });
      let filtered = res.data.orders || [];
      if (filterChannel) filtered = filtered.filter((o: LogisticsOrder) => o.channel === filterChannel);
      if (filterOrderStatus) filtered = filtered.filter((o: LogisticsOrder) => o.status === filterOrderStatus);
      setOrders(filtered);
      setTotalOrders(filtered.length);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to fetch orders'));
      setOrders([]);
      setTotalOrders(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => fetchOrders();
  const handleClearFilters = () => {
    setSearchQuery(''); setFilterStatus(''); setFilterOrderStatus('');
    setFilterChannel(''); setFilterProvider(''); setFilterDateFrom(''); setFilterDateTo('');
    setTrackAwb(''); setTrackResult(null); setTrackError('');
    setPincodeCheck(''); setPincodeResult(null);
    setServiceabilityResult(null); setSyncResult(null); setResult(null);
    setTimeout(fetchOrders, 0);
  };

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedIds.size === orders.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(orders.map(o => o.id)));
  };

  const openDCModal = async () => {
    setShowDCModal(true);
    try {
      const res = await api.get('/inventory/warehouses/');
      const whs = res.data || [];
      setWarehouses(whs);
      const def = whs.find((w: any) => w.is_default) || whs[0];
      if (def) setDcForm(prev => ({ ...prev, warehouse_id: def.id }));
    } catch (e) { /* ignore */ }
  };

  const handleGenerateDC = async () => {
    if (selectedIds.size === 0) return;
    setProcessing(true);
    setResult(null);
    try {
      const res = await api.post('/logistics/generate-dc', {
        order_ids: Array.from(selectedIds),
        warehouse_id: dcForm.warehouse_id || undefined,
        vehicle_number: dcForm.vehicle_number || undefined,
        transport_mode: dcForm.transport_mode,
      });
      setResult(res.data);
      if (res.data.success_count > 0) { setSelectedIds(new Set()); setSelectedOrder(null); fetchOrders(); }
      setShowDCModal(false);
    } catch (err: any) {
      setResult({ success_count: 0, failed_count: selectedIds.size, dc_numbers: [], results: [{ success: false, error: extractErrorMessage(err, 'Failed') }] });
    } finally { setProcessing(false); }
  };

  const handleExportOrders = async () => {
    if (selectedIds.size === 0) return;
    try {
      const res = await api.post('/logistics/export-orders', {
        order_ids: Array.from(selectedIds),
      }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `logistics_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      alert('Export failed');
    }
  };

  const handleImportAWB = async (file?: File) => {
    const fileToUpload = file || importFile;
    if (!fileToUpload) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', fileToUpload);
      const res = await api.post('/logistics/import-awb', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      if (res.data.success_count > 0) {
        fetchOrders();
      }
      setImportFile(null);
      // Reset the file input
      const fileInput = document.getElementById('import-awb-file') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (err: any) {
      setImportResult({ success_count: 0, failed_count: 1, errors: [{ error: err.response?.data?.detail || 'Import failed' }] });
    } finally {
      setImporting(false);
    }
  };

  const handleTrackAWB = async () => {
    if (!trackAwb.trim()) return;
    setTracking(true);
    setTrackResult(null);
    setTrackError('');
    try {
      const res = await api.post('/logistics/track-awb', {
        awb_number: trackAwb.trim(),
        carrier: trackCarrier,
      });
      setTrackResult(res.data);
    } catch (err: any) {
      setTrackError(err.response?.data?.detail || 'Tracking failed');
    } finally {
      setTracking(false);
    }
  };

  const handleTrackOrderAWB = async (orderId: number) => {
    setTracking(true);
    setTrackResult(null);
    setTrackError('');
    try {
      const res = await api.get(`/logistics/awb/${orderId}/track`);
      const data = res.data;
      const tracking = data.tracking || {};
      setTrackResult({
        awb_number: data.awb_number,
        carrier: (data.provider || '').toLowerCase(),
        parsed: {
          status: tracking.status || 'Unknown',
          origin: tracking.origin || '',
          destination: tracking.destination || '',
          expected_delivery: tracking.expected_delivery || '',
          weight: tracking.weight || '',
          scans: tracking.scans || [],
        },
        raw_xml: data.raw_xml || '',
        success: true,
      });
    } catch (err: any) {
      setTrackError(err.response?.data?.detail || 'Tracking failed');
    } finally {
      setTracking(false);
    }
  };

  const handleCheckPincode = async () => {
    if (!pincodeCheck.trim() || pincodeCheck.trim().length !== 6) return;
    setPincodeChecking(true);
    setPincodeResult(null);
    try {
      const res = await api.post('/logistics/check-pincode', {
        pincode: pincodeCheck.trim(),
        carrier: pincodeCarrier,
      });
      setPincodeResult(res.data);
    } catch (err: any) {
      setPincodeResult({ serviceable: false, error: err.response?.data?.detail || 'Check failed' });
    } finally {
      setPincodeChecking(false);
    }
  };

  const handleBulkServiceabilityCheck = async (orderIds?: number[]) => {
    const ids = orderIds || Array.from(selectedIds);
    if (ids.length === 0) return;
    setServiceabilityChecking(true);
    setServiceabilityResult(null);
    try {
      const res = await api.post('/logistics/check-serviceability-bulk', {
        order_ids: ids,
        carrier: serviceabilityCarrier,
      });
      setServiceabilityResult(res.data);
    } catch (err: any) {
      setServiceabilityResult({ error: err.response?.data?.detail || 'Serviceability check failed' });
    } finally {
      setServiceabilityChecking(false);
    }
  };

  const handleSingleServiceabilityCheck = async (orderId: number) => {
    handleBulkServiceabilityCheck([orderId]);
  };

  const handleBookWithProvider = async (orderId: number) => {
    if (!orderId) return;
    setProcessing(true);
    try {
      const res = await api.post('/logistics/book-awb-provider', {
        order_id: orderId,
        carrier: bookingProvider,
        shipping_mode: bookingProvider === 'delhivery' ? bookingShippingMode : '',
      });
      setResult({ success_count: 1, failed_count: 0, results: [{ success: true, order_id: orderId, awb_number: res.data.awb_number, message: res.data.message }] });
      fetchOrders();
    } catch (err: any) {
      setResult({ success_count: 0, failed_count: 1, results: [{ success: false, error: err.response?.data?.detail || 'Booking failed' }] });
    } finally {
      setProcessing(false);
    }
  };

  const viewPDF = async (dcId: number) => {
    try {
      const res = await api.get(`/logistics/dc/${dcId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e) { alert('Failed to load PDF'); }
  };

  const downloadDC = async (dcId: number, dcNumber: string) => {
    try {
      const res = await api.get(`/logistics/dc/${dcId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `DC_${dcNumber.replace(/\//g, '-')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert('Failed to download DC'); }
  };

  const printDC = async (dcId: number) => {
    try {
      const res = await api.get(`/logistics/dc/${dcId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => { printWindow.print(); });
      }
    } catch (e) { alert('Failed to print DC'); }
  };

  const downloadAWB = async (orderId: number, format: 'thermal' | 'a4' = 'thermal') => {
    try {
      const endpoint = format === 'a4' 
        ? `/logistics/label/${orderId}/a4`
        : `/logistics/label/${orderId}/thermal`;
      const res = await api.get(endpoint, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const order = orders.find(o => o.id === orderId);
      link.setAttribute('download', `${format}_label_${order?.awb_number || orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Label download failed');
    }
  };

  const printAWB = async (orderId: number, format: 'thermal' | 'a4' = 'thermal') => {
    try {
      const endpoint = format === 'a4'
        ? `/logistics/label/${orderId}/a4`
        : `/logistics/label/${orderId}/thermal`;
      const res = await api.get(endpoint, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => { printWindow.print(); });
      }
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Label print failed');
    }
  };

  // AWB Generation Modal state
  const [showAwbModal, setShowAwbModal] = useState(false);
  const [awbOrderIds, setAwbOrderIds] = useState<number[]>([]);
  const [awbPackages, setAwbPackages] = useState<any[]>([]);
  const [awbForm, setAwbForm] = useState({ weight: '2', length: '10', width: '10', height: '10', declared_value: '2500', package_id: '', carrier: 'delhivery', shipping_mode: 'Surface' });
  const [awbChannelDefaults, setAwbChannelDefaults] = useState<any>(null);

  const openAwbModal = async (orderIds: number[]) => {
    setAwbOrderIds(orderIds);
    setAwbForm({ weight: '2', length: '10', width: '10', height: '10', declared_value: '2500', package_id: '', carrier: bookingProvider, shipping_mode: bookingShippingMode });
    setAwbChannelDefaults(null);

    // Fetch packages
    try {
      const res = await api.get('/packages/?active_only=true');
      setAwbPackages(Array.isArray(res.data) ? res.data : []);
    } catch { setAwbPackages([]); }

    // Check if the order(s) have a channel with default dimensions
    const firstOrder = orders.find(o => orderIds.includes(o.id));
    if (firstOrder?.channel) {
      try {
        const chRes = await api.get('/channels/');
        const channels = chRes.data || [];
        const ch = channels.find((c: any) => c.name === firstOrder.channel || c.code === firstOrder.channel);
        if (ch && ch.default_length && ch.default_width && ch.default_height) {
          setAwbChannelDefaults(ch);
          const channelCarrier = ch.carrier_config?.default_carrier || bookingProvider;
          const channelMode = ch.carrier_config?.carriers?.[channelCarrier]?.shipping_mode || bookingShippingMode;
          setAwbForm({
            weight: String(ch.default_weight || 0.5),
            length: String(ch.default_length),
            width: String(ch.default_width),
            height: String(ch.default_height),
            declared_value: ch.default_declared_value ? String(ch.default_declared_value) : '',
            package_id: '',
            carrier: channelCarrier,
            shipping_mode: channelMode,
          });
        }
      } catch { /* ignore */ }
    }

    setShowAwbModal(true);
  };

  const handlePackageSelect = (pkgId: string) => {
    setAwbForm(prev => ({ ...prev, package_id: pkgId }));
    if (pkgId) {
      const pkg = awbPackages.find((p: any) => p.id === parseInt(pkgId));
      if (pkg) {
        setAwbForm(prev => ({
          ...prev,
          package_id: pkgId,
          weight: String(pkg.weight),
          length: String(pkg.length),
          width: String(pkg.width),
          height: String(pkg.height),
          declared_value: pkg.declared_value ? String(pkg.declared_value) : '',
        }));
      }
    }
  };

  const handleAwbSubmit = async () => {
    setShowAwbModal(false);
    setProcessing(true);
    setResult(null);

    const carrier = awbForm.carrier;
    const shippingMode = carrier === 'delhivery' ? awbForm.shipping_mode : '';

    try {
      if (awbOrderIds.length === 1) {
        const res = await api.post('/logistics/book-awb-provider', {
          order_id: awbOrderIds[0],
          carrier: carrier,
          shipping_mode: shippingMode,
        });
        setResult({ success_count: 1, failed_count: 0, results: [{ ...res.data, success: true }] });
      } else {
        let successCount = 0;
        let failCount = 0;
        const results: any[] = [];
        for (const orderId of awbOrderIds) {
          try {
            const res = await api.post('/logistics/book-awb-provider', {
              order_id: orderId,
              carrier: carrier,
              shipping_mode: shippingMode,
            });
            results.push({ ...res.data, success: true, order_id: orderId });
            successCount++;
          } catch (err: any) {
            results.push({ success: false, order_id: orderId, error: err.response?.data?.detail || 'Failed' });
            failCount++;
          }
        }
        setResult({ success_count: successCount, failed_count: failCount, results });
        if (successCount > 0) setSelectedIds(new Set());
      }
      fetchOrders();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'AWB generation failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleGenerateAWB = (orderId: number) => {
    openAwbModal([orderId]);
  };

  const handleBulkGenerateAWB = () => {
    if (selectedIds.size === 0) return;
    openAwbModal(Array.from(selectedIds));
  };

  const handleBulkPrintLabels = async (format: 'thermal' | 'a4') => {
    if (selectedIds.size === 0) return;
    try {
      const endpoint = format === 'a4' ? '/logistics/labels/a4/bulk' : '/logistics/labels/thermal/bulk';
      const res = await api.post(endpoint, { order_ids: Array.from(selectedIds) }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.addEventListener('load', () => { printWindow.print(); });
      }
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Bulk label generation failed');
    }
  };

  const handleCancelAWB = async (orderId: number) => {
    if (!cancelConfirm.id) return;
    try {
      const res = await api.post(`/logistics/awb/${cancelConfirm.id}/cancel`);
      setCancelConfirm({show: false, id: null, name: ''});
      alert(res.data.message || 'AWB cancelled');
      fetchOrders();
      if (selectedOrder?.id === cancelConfirm.id) setSelectedOrder(null);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'AWB cancellation failed');
    }
  };

  const handleCancelAWBClick = (orderId: number, awb: string) => {
    // Block cancellation for shipments already picked up or in transit
    const order = orders.find(o => o.id === orderId) || selectedOrder;
    const nonCancellableStatuses = ['picked_up', 'in_transit', 'out_for_delivery', 'delivered'];
    if (order && order.shipping_status && nonCancellableStatuses.includes(order.shipping_status)) {
      alert(`Cannot cancel AWB — shipment is already "${order.shipping_status.replace(/_/g, ' ')}". Contact the carrier directly.`);
      return;
    }
    setCancelConfirm({show: true, id: orderId, name: awb});
  };

  const handleSyncShipping = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const order_ids = selectedIds.size > 0 ? Array.from(selectedIds) : [];
      const res = await api.post('/logistics/shipping/sync', { order_ids });
      setSyncResult(res.data);
      fetchOrders();
    } catch (err: any) {
      const errorDetail = err.response?.data?.detail || err.response?.data || err.message || 'Sync failed';
      setSyncResult({ error: typeof errorDetail === 'string' ? errorDetail : JSON.stringify(errorDetail) });
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (d: string | null | undefined) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleOpenPickupModal = async () => {
    setPickupResult(null);
    // Fetch pending count
    try {
      const res = await api.get('/logistics/pickup/pending-count');
      setPendingPickupCount(res.data);
    } catch { setPendingPickupCount(null); }
    // Default date to today
    const today = new Date().toISOString().split('T')[0];
    setPickupForm({ date: today, time: '14:00', carrier: 'delhivery' });
    setShowPickupModal(true);
  };

  const handleSchedulePickup = async () => {
    setPickupScheduling(true);
    setPickupResult(null);
    try {
      const res = await api.post('/logistics/pickup/schedule', {
        carrier: pickupForm.carrier,
        pickup_date: pickupForm.date,
        pickup_time: pickupForm.time + ':00',
      });
      setPickupResult(res.data);
    } catch (err: any) {
      setPickupResult({ success: false, error: err.response?.data?.detail || 'Pickup scheduling failed' });
    } finally {
      setPickupScheduling(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const sortedOrders = [...orders].sort((a, b) => {
    // Primary sort
    let aVal: any = (a as any)[sortField] || '';
    let bVal: any = (b as any)[sortField] || '';
    if (sortField === 'order_date' || sortField === 'dc_date') {
      aVal = aVal ? new Date(aVal).getTime() : 0;
      bVal = bVal ? new Date(bVal).getTime() : 0;
    } else if (sortField === 'total_amount') {
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
    } else {
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
    }
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    
    // Secondary sort: order_date desc (if primary wasn't order_date)
    if (sortField !== 'order_date') {
      const aDate = a.order_date ? new Date(a.order_date).getTime() : 0;
      const bDate = b.order_date ? new Date(b.order_date).getTime() : 0;
      if (aDate !== bDate) return bDate - aDate;
    }
    
    // Tertiary sort: order status
    const statusOrder: Record<string, number> = { pending: 0, confirmed: 1, allocated: 2, shipped: 3, delivered: 4, cancelled: 5 };
    const aStatus = statusOrder[a.status] ?? 99;
    const bStatus = statusOrder[b.status] ?? 99;
    return aStatus - bStatus;
  });

  const getSortIcon = (field: string) => {
    if (sortField !== field) return ' ↕';
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  const canGenerateDC = selectedIds.size > 0 && orders.filter(o => selectedIds.has(o.id)).every(o => !o.dc_number);
  const canGenerateAWB = selectedIds.size > 0 && orders.filter(o => selectedIds.has(o.id)).every(o => o.dc_number && !o.awb_number);
  const channels = Array.from(new Set(orders.map(o => o.channel).filter(Boolean))) as string[];

  // Pagination
  const totalPages = Math.ceil(sortedOrders.length / pageSize);
  const paginatedOrders = sortedOrders.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const getStatusBadge = (status: string) => (
    <span className={`logistics-status-badge status-${status}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );

  if (loading && orders.length === 0) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}><LoadingSpinner size="large" message="Loading logistics..." /></div>;
  }

  return (
    <div className="logistics-dashboard">
      {/* Always visible filters */}
      <div className="logistics-filter-bar">
        <div className="logistics-filter-row">
          <div className="logistics-filter-fields">
            <div className="logistics-filter-group w-awb">
              <label className="logistics-filter-label">Order / AWB / Customer</label>
              <input className="logistics-filter-input" type="text" placeholder="Search order #, AWB, customer name, email, phone..."
                value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()} />
            </div>
            <div className="logistics-filter-group w-sm">
              <label className="logistics-filter-label">Shipping Status</label>
              <select className="logistics-filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value="">All</option>
                {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div className="logistics-filter-group w-sm">
              <label className="logistics-filter-label">Order Status</label>
              <select className="logistics-filter-select" value={filterOrderStatus} onChange={e => setFilterOrderStatus(e.target.value)}>
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="confirmed">Confirmed</option>
                <option value="allocated">Allocated</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div className="logistics-filter-group w-sm">
              <label className="logistics-filter-label">Channel</label>
              <select className="logistics-filter-select" value={filterChannel} onChange={e => setFilterChannel(e.target.value)}>
                <option value="">All Channels</option>
                {channels.map(ch => <option key={ch} value={ch}>{ch}</option>)}
              </select>
            </div>
            <div className="logistics-filter-group w-sm">
              <label className="logistics-filter-label">Partner</label>
              <select className="logistics-filter-select" value={filterProvider} onChange={e => setFilterProvider(e.target.value)}>
                <option value="">All Partners</option>
                <option value="BlueDart">BlueDart</option>
                <option value="Delhivery">Delhivery</option>
                <option value="Custom">Custom</option>
                <option value="Hand-Delivery">Hand-Delivery</option>
              </select>
            </div>
            <button className="logistics-btn logistics-btn-primary" onClick={handleSearch} style={{ alignSelf: 'flex-end' }}>Search</button>
            <button className="logistics-btn logistics-btn-secondary" onClick={handleClearFilters} style={{ alignSelf: 'flex-end' }}>Clear</button>
            <input type="file" id="import-awb-file" accept=".csv,.xlsx,.xls" onChange={(e) => { setImportFile(e.target.files?.[0] || null); if (e.target.files?.[0]) handleImportAWB(e.target.files[0]); }} style={{ display: 'none' }} />
            <button className="logistics-btn logistics-btn-secondary" onClick={() => document.getElementById('import-awb-file')?.click()} disabled={importing} style={{ alignSelf: 'flex-end', fontSize: '12px' }}>
              {importing ? 'Importing...' : 'Import AWB'}
            </button>
            {importResult && (
              <span style={{ alignSelf: 'flex-end', fontSize: '12px' }}>
                <strong style={{ color: '#16a34a' }}>{importResult.success_count} updated</strong>
                {importResult.failed_count > 0 && <span style={{ color: '#dc2626', marginLeft: '6px' }}>{importResult.failed_count} failed</span>}
                <button onClick={() => setImportResult(null)} style={{ marginLeft: '6px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '11px', color: '#64748b' }}>✕</button>
              </span>
            )}
            <button className="logistics-btn logistics-btn-secondary" onClick={handleSyncShipping} disabled={syncing} style={{ alignSelf: 'flex-end', fontSize: '12px' }}
              title={selectedIds.size > 0 ? `Sync ${selectedIds.size} selected orders` : 'Sync all orders not yet delivered'}>
              {syncing ? 'Syncing...' : `Sync Status${selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}`}
            </button>
            <button className="logistics-btn logistics-btn-secondary" onClick={handleOpenPickupModal} style={{ alignSelf: 'flex-end', fontSize: '12px' }}>
              Schedule Pickup
            </button>
          </div>
        </div>
      </div>

      {/* Collapsible: Date, Sync, Import, Track, Pincode */}
      <CollapsibleFilters label="More: Date, Track AWB, Pincode, Import">
      <div style={{ padding: '10px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        {/* Date filters + Sync */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '10px' }}>
          <div>
            <label className="logistics-filter-label">From Date</label>
            <input className="logistics-filter-input" type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} />
          </div>
          <div>
            <label className="logistics-filter-label">To Date</label>
            <input className="logistics-filter-input" type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} />
          </div>
        </div>

        {/* Track AWB + Check Pincode — clearly separated */}
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', paddingTop: '10px', borderTop: '1px solid #e2e8f0' }}>
          {/* Track AWB */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
            <div>
              <label style={{ fontSize: '11px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '3px' }}>Track AWB</label>
              <input type="text" value={trackAwb} onChange={(e) => setTrackAwb(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleTrackAWB()} placeholder="AWB number" style={{ padding: '7px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', width: '180px' }} />
            </div>
            <select value={trackCarrier} onChange={(e) => setTrackCarrier(e.target.value)} style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '12px' }}>
              <option value="bluedart">BlueDart</option>
              <option value="delhivery">Delhivery</option>
            </select>
            <button className="logistics-btn logistics-btn-primary logistics-btn-sm" onClick={handleTrackAWB} disabled={tracking || !trackAwb.trim()} style={{ fontSize: '12px' }}>
              {tracking ? '...' : 'Track'}
            </button>
            {trackError && <span style={{ fontSize: '11px', color: '#dc2626' }}>{trackError}</span>}
          </div>
        </div>

      </div>
      {/* Track Result */}
      {trackResult && (
        <div style={{ padding: '14px 20px', background: '#f0fdf4', borderBottom: '1px solid #bbf7d0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '14px', fontWeight: 700, color: '#166534' }}>AWB: {trackResult.awb_number} — {trackResult.parsed?.status || 'Unknown'}</span>
            <button onClick={() => setTrackResult(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b', fontSize: '18px' }}>×</button>
          </div>
          {trackResult.parsed && (
            <div className="lg-result-grid">
              {trackResult.parsed.origin && <div><div style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase' }}>Origin</div><div style={{ fontSize: '13px', fontWeight: 500 }}>{trackResult.parsed.origin}</div></div>}
              {trackResult.parsed.destination && <div><div style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase' }}>Destination</div><div style={{ fontSize: '13px', fontWeight: 500 }}>{trackResult.parsed.destination}</div></div>}
              {trackResult.parsed.expected_delivery && <div><div style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase' }}>Expected Delivery</div><div style={{ fontSize: '13px', fontWeight: 500 }}>{trackResult.parsed.expected_delivery}</div></div>}
              {trackResult.parsed.weight && <div><div style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase' }}>Weight</div><div style={{ fontSize: '13px', fontWeight: 500 }}>{trackResult.parsed.weight} kg</div></div>}
            </div>
          )}
          {trackResult.parsed?.scans?.length > 0 && (
            <div style={{ maxHeight: '180px', overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
              <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                <thead><tr style={{ background: '#f8fafb' }}>
                  <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Date/Time</th>
                  <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Location</th>
                  <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b' }}>Activity</th>
                </tr></thead>
                <tbody>
                  {trackResult.parsed.scans.map((scan: any, idx: number) => (
                    <tr key={idx} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '5px 10px', whiteSpace: 'nowrap' }}>{scan.ScanDate || scan.Date || scan.date || ''} {scan.ScanTime || scan.Time || ''}</td>
                      <td style={{ padding: '5px 10px' }}>{scan.ScannedLocation || scan.Location || scan.location || ''}</td>
                      <td style={{ padding: '5px 10px' }}>{scan.Scan || scan.ScanType || scan.Instructions || scan.description || scan.Activity || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {(!trackResult.parsed?.scans || trackResult.parsed.scans.length === 0) && trackResult.raw_xml && (
            <details style={{ fontSize: '11px', color: '#64748b' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 500 }}>Raw Response</summary>
              <pre style={{ marginTop: '6px', padding: '8px', background: '#f8fafb', borderRadius: '4px', maxHeight: '150px', overflow: 'auto', whiteSpace: 'pre-wrap' }}>{trackResult.raw_xml}</pre>
            </details>
          )}
        </div>
      )}
      </CollapsibleFilters>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="lg-bulk-bar">
          <strong style={{ fontSize: '13px', color: '#0369a1' }}>{selectedIds.size} selected</strong>
          <button className="logistics-btn logistics-btn-primary logistics-btn-sm" onClick={openDCModal} disabled={processing || !canGenerateDC}
            title={canGenerateDC ? '' : 'Some orders already have DC'}>
            Generate DC
          </button>
          <button className="logistics-btn logistics-btn-primary logistics-btn-sm" onClick={handleBulkGenerateAWB} disabled={processing || !canGenerateAWB}
            title={canGenerateAWB ? '' : 'All selected orders must have DC generated and no existing AWB'}>
            Generate AWB
          </button>
          <select value={bookingProvider} onChange={(e) => setBookingProvider(e.target.value)} style={{ padding: '4px 8px', fontSize: '11px', border: '1px solid #7dd3fc', borderRadius: '4px', background: 'white' }}>
            <option value="bluedart">BlueDart</option>
            <option value="delhivery">Delhivery</option>
          </select>
          {bookingProvider === 'delhivery' && (
            <select value={bookingShippingMode} onChange={(e) => setBookingShippingMode(e.target.value)} style={{ padding: '4px 8px', fontSize: '11px', border: '1px solid #7dd3fc', borderRadius: '4px', background: 'white' }}>
              <option value="Surface">Surface</option>
              <option value="Express">Express</option>
            </select>
          )}
          <button className="logistics-btn logistics-btn-secondary logistics-btn-sm" onClick={() => handleBulkPrintLabels('thermal')} title="Print thermal labels (6x4)">
            🖨 Labels
          </button>
          {bookingProvider === 'bluedart' && (
            <button className="logistics-btn logistics-btn-secondary logistics-btn-sm" onClick={() => handleBulkPrintLabels('a4')} title="Print A4 labels (2 per page)">
              🖨 A4
            </button>
          )}
          <button className="logistics-btn logistics-btn-primary logistics-btn-sm" onClick={handleExportOrders}>
            Export
          </button>
          <select value={serviceabilityCarrier} onChange={(e) => setServiceabilityCarrier(e.target.value)} style={{ padding: '4px 8px', fontSize: '11px', border: '1px solid #7dd3fc', borderRadius: '4px', background: 'white' }}>
            <option value="all">All Carriers</option>
            <option value="bluedart">BlueDart</option>
            <option value="delhivery">Delhivery</option>
          </select>
          <button className="logistics-btn logistics-btn-secondary logistics-btn-sm" onClick={() => handleBulkServiceabilityCheck()} disabled={serviceabilityChecking} style={{ fontSize: '12px' }}>
            {serviceabilityChecking ? 'Checking...' : 'Check Serviceability'}
          </button>
          <button className="logistics-btn logistics-btn-secondary logistics-btn-sm" onClick={() => setSelectedIds(new Set())} style={{ marginLeft: 'auto' }}>
            Clear Selection
          </button>
        </div>
      )}

      {/* Result banner */}
      {result && (
        <div style={{ padding: '10px 20px', background: result.failed_count === 0 ? '#d1fae5' : '#fef3c7', borderBottom: '1px solid #6ee7b7', fontSize: '13px' }}>
          <strong>{result.success_count} succeeded</strong>
          {result.failed_count > 0 && <span>, <strong style={{ color: '#dc2626' }}>{result.failed_count} failed</strong></span>}
          {result.results?.filter((r: any) => r.success && r.dc_id).map((r: any) => (
            <button key={r.dc_id} onClick={() => viewPDF(r.dc_id)} style={{ marginLeft: '6px', color: '#0d9488', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none', fontSize: '12px' }}>
              📄 {r.dc_number}
            </button>
          ))}
          {result.results?.filter((r: any) => !r.success).map((r: any, i: number) => (
            <span key={i} style={{ marginLeft: '8px', color: '#dc2626', fontSize: '12px' }}>{r.order_number}: {r.error}</span>
          ))}
          <button onClick={() => setResult(null)} style={{ marginLeft: '12px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '12px', color: '#64748b' }}>✕</button>
        </div>
      )}

      {/* Sync result banner */}
      {syncResult && (
        <div style={{ padding: '10px 20px', background: syncResult.error ? '#fef2f2' : '#dbeafe', borderBottom: '1px solid #93c5fd', fontSize: '13px' }}>
          {syncResult.error ? (
            <span style={{ color: '#dc2626' }}>{syncResult.error}</span>
          ) : (
            <>
              <strong>Sync complete:</strong> {syncResult.total_synced} checked, <strong style={{ color: '#16a34a' }}>{syncResult.updated} updated</strong>
              {syncResult.errors > 0 && (
                <span>
                  , <strong style={{ color: '#dc2626' }}>{syncResult.errors} errors</strong>
                  <button onClick={() => setSyncResult({ ...syncResult, _showErrors: !syncResult._showErrors })}
                    style={{ marginLeft: '6px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '12px', color: '#dc2626', textDecoration: 'underline' }}>
                    {syncResult._showErrors ? 'hide' : 'show details'}
                  </button>
                </span>
              )}
              {syncResult.results?.filter((r: any) => r.status === 'updated').slice(0, 5).map((r: any, i: number) => (
                <span key={i} style={{ marginLeft: '8px', fontSize: '12px' }}>
                  {r.order_number}: {r.old_shipping_status} → {r.new_shipping_status}
                </span>
              ))}
              {syncResult._showErrors && syncResult.results?.filter((r: any) => !r.success || r.error).length > 0 && (
                <div style={{ marginTop: '8px', padding: '8px 12px', background: '#fef2f2', borderRadius: '6px', maxHeight: '200px', overflow: 'auto' }}>
                  {syncResult.results.filter((r: any) => !r.success || r.error).map((r: any, i: number) => (
                    <div key={i} style={{ fontSize: '12px', color: '#dc2626', marginBottom: '4px' }}>
                      <strong>{r.order_number || `Order #${r.order_id}`}:</strong> {r.error || 'Unknown error'}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          <button onClick={() => setSyncResult(null)} style={{ marginLeft: '12px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '12px', color: '#64748b' }}>✕</button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ padding: '0 20px', marginTop: '12px' }}>
          <ErrorDisplay error={error} onRetry={fetchOrders} onDismiss={() => setError(null)} />
        </div>
      )}

      {/* Serviceability Results */}
      {serviceabilityResult && (
        <div style={{ padding: '14px 20px', background: '#f0fdf4', borderBottom: '1px solid #bbf7d0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '14px', fontWeight: 700, color: '#166534' }}>
              Serviceability Check — {serviceabilityResult.serviceable_count || 0} of {serviceabilityResult.total || 0} serviceable
              {serviceabilityResult.not_serviceable_count > 0 && (
                <span style={{ color: '#dc2626', marginLeft: '8px' }}>({serviceabilityResult.not_serviceable_count} not serviceable)</span>
              )}
            </span>
            <button onClick={() => setServiceabilityResult(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b', fontSize: '18px' }}>×</button>
          </div>
          {serviceabilityResult.error && !serviceabilityResult.results && (
            <div style={{ color: '#dc2626', fontSize: '13px' }}>{serviceabilityResult.error}</div>
          )}
          {serviceabilityResult.results && (
            <div style={{ maxHeight: '300px', overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
              <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafb' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Order No</th>
                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pincode</th>
                    {(serviceabilityCarrier === 'all' || serviceabilityCarrier === 'delhivery') && (
                      <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Delhivery</th>
                    )}
                    {(serviceabilityCarrier === 'all' || serviceabilityCarrier === 'bluedart') && (
                      <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#64748b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>BlueDart</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {serviceabilityResult.results.map((r: any, idx: number) => (
                    <tr key={idx} style={{ borderTop: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 500, fontFamily: "'SF Mono', 'Fira Code', monospace", fontSize: '12px' }}>{r.order_number}</td>
                      <td style={{ padding: '8px 12px', fontFamily: "'SF Mono', 'Fira Code', monospace", fontSize: '12px', color: '#64748b' }}>{r.pincode || '—'}</td>
                      {(serviceabilityCarrier === 'all' || serviceabilityCarrier === 'delhivery') && (
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          {r.error ? (
                            <span style={{ color: '#dc2626', fontSize: '11px' }}>{r.error}</span>
                          ) : r.delhivery ? (
                            r.delhivery.error ? (
                              <span style={{ display: 'inline-block', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, background: '#f8f9fa', color: '#64748b' }}>
                                Unknown
                              </span>
                            ) : (
                              <span style={{ display: 'inline-block', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, background: r.delhivery.serviceable ? '#d4edda' : '#f8d7da', color: r.delhivery.serviceable ? '#155724' : '#721c24' }}>
                                {r.delhivery.serviceable ? 'Serviceable' : 'Not Serviceable'}
                              </span>
                            )
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: '11px' }}>—</span>
                          )}
                        </td>
                      )}
                      {(serviceabilityCarrier === 'all' || serviceabilityCarrier === 'bluedart') && (
                        <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                          {r.error ? (
                            <span style={{ color: '#dc2626', fontSize: '11px' }}>{r.error}</span>
                          ) : r.bluedart ? (
                            r.bluedart.error ? (
                              <span style={{ display: 'inline-block', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, background: '#f8f9fa', color: '#64748b' }}>
                                Unknown
                              </span>
                            ) : (
                              <span style={{ display: 'inline-block', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 600, background: r.bluedart.serviceable ? '#d4edda' : '#f8d7da', color: r.bluedart.serviceable ? '#155724' : '#721c24' }}>
                                {r.bluedart.serviceable ? 'Serviceable' : 'Not Serviceable'}
                                {r.bluedart.details?.[0]?.transit_days && ` (${r.bluedart.details[0].transit_days}d)`}
                              </span>
                            )
                          ) : (
                            <span style={{ color: '#94a3b8', fontSize: '11px' }}>—</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div className="logistics-table-wrap">
        <table className="logistics-table">
          <thead>
            <tr>
              <th style={{ width: '40px' }}>
                <input type="checkbox" checked={orders.length > 0 && selectedIds.size === orders.length} onChange={toggleSelectAll} />
              </th>
              <th onClick={() => handleSort('order_number')} style={{ cursor: 'pointer' }}>Order No{getSortIcon('order_number')}</th>
              <th onClick={() => handleSort('channel')} style={{ cursor: 'pointer' }}>Channel{getSortIcon('channel')}</th>
              <th onClick={() => handleSort('order_date')} style={{ cursor: 'pointer' }}>Order Date{getSortIcon('order_date')}</th>
              <th onClick={() => handleSort('ship_date')} style={{ cursor: 'pointer' }}>Shipped Date{getSortIcon('ship_date')}</th>
              <th onClick={() => handleSort('delivered_date')} style={{ cursor: 'pointer' }}>Delivered Date{getSortIcon('delivered_date')}</th>
              <th onClick={() => handleSort('shipping_provider')} style={{ cursor: 'pointer' }}>Partner{getSortIcon('shipping_provider')}</th>
              <th onClick={() => handleSort('awb_number')} style={{ cursor: 'pointer' }}>AWB{getSortIcon('awb_number')}</th>
              <th onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>Order Status{getSortIcon('status')}</th>
              <th onClick={() => handleSort('shipping_status')} style={{ cursor: 'pointer' }}>Shipping Status{getSortIcon('shipping_status')}</th>
              <th className="sticky-col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr className="empty-row">
                <td colSpan={11}>
                  {loading ? 'Loading...' : 'No orders found. Orders appear here once allocated.'}
                </td>
              </tr>
            ) : (
              paginatedOrders.map(order => (
                <tr key={order.id} style={{ background: selectedIds.has(order.id) ? '#dbeafe' : undefined, cursor: 'pointer' }}
                  onClick={() => setSelectedOrder(order)}>
                  <td onClick={e => { e.stopPropagation(); toggleSelect(order.id); }} style={{ cursor: 'pointer' }}>
                    <input type="checkbox" checked={selectedIds.has(order.id)} onChange={() => {}} />
                  </td>
                  <td>
                    <span className="awb-code">{order.order_number}</span>
                    {order.external_order_id && (
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{order.external_order_id}</div>
                    )}
                  </td>
                  <td>{order.channel || '-'}</td>
                  <td>{formatDate(order.order_date)}</td>
                  <td>{order.ship_date ? formatDate(order.ship_date) : <span style={{ color: '#94a3b8' }}>—</span>}</td>
                  <td>{(order as any).delivered_date ? formatDate((order as any).delivered_date) : <span style={{ color: '#94a3b8' }}>—</span>}</td>
                  <td style={{ fontSize: '12px', color: '#475569' }}>{order.shipping_provider || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                  <td>{order.awb_number || <span style={{ color: '#94a3b8' }}>—</span>}</td>
                  <td>
                    <span className={`logistics-status-badge status-${order.status}`}>
                      {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                    </span>
                  </td>
                  <td>
                    {order.shipping_status ? (
                      <span className={`logistics-status-badge status-${order.shipping_status}`}>
                        {order.shipping_status.charAt(0).toUpperCase() + order.shipping_status.slice(1).replace('_', ' ')}
                      </span>
                    ) : (
                      <span style={{ color: '#94a3b8' }}>—</span>
                    )}
                  </td>
                  <td className="sticky-col">
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {!order.dc_number ? (
                        <button className="logistics-btn logistics-btn-primary logistics-btn-sm"
                          onClick={(e) => { e.stopPropagation(); setSelectedIds(new Set([order.id])); openDCModal(); }}
                          title="Generate DC">
                          DC
                        </button>
                      ) : (
                        <>
                          <button className="logistics-btn logistics-btn-secondary logistics-btn-sm"
                            onClick={(e) => { e.stopPropagation(); printDC(order.dc_id!); }}
                            title="Print DC">
                            🖨 DC
                          </button>
                        </>
                      )}
                      {!order.awb_number ? (
                        <button className="logistics-btn logistics-btn-primary logistics-btn-sm"
                          onClick={(e) => { e.stopPropagation(); handleGenerateAWB(order.id); }}
                          title={order.dc_number ? 'Generate AWB' : "Generate DC first"}
                          disabled={processing || !order.dc_number}>
                          AWB
                        </button>
                      ) : (
                        <>
                          <button className="logistics-btn logistics-btn-secondary logistics-btn-sm"
                            onClick={(e) => { e.stopPropagation(); printAWB(order.id, 'thermal'); }}
                            title="Print thermal label (6x4)">
                            🖨
                          </button>
                          {order.shipping_provider?.toLowerCase() === 'bluedart' && (
                            <button className="logistics-btn logistics-btn-secondary logistics-btn-sm"
                              onClick={(e) => { e.stopPropagation(); downloadAWB(order.id, 'a4'); }}
                              title="Download A4 label">
                              A4
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="logistics-pagination" style={{ flexWrap: 'wrap', gap: '10px' }}>
        <span style={{ fontSize: '13px', color: '#64748b' }}>
          {sortedOrders.length > 0
            ? `Showing ${(currentPage - 1) * pageSize + 1} to ${Math.min(currentPage * pageSize, sortedOrders.length)} of ${sortedOrders.length} orders`
            : 'No records'}
        </span>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <label style={{ fontSize: '13px', color: '#64748b' }}>Rows per page:</label>
          <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
            style={{ padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '13px' }}>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <button className="logistics-btn logistics-btn-secondary logistics-btn-sm"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
            Previous
          </button>
          <span style={{ fontSize: '13px', color: '#334155' }}>Page {currentPage} of {totalPages || 1}</span>
          <button className="logistics-btn logistics-btn-primary logistics-btn-sm"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages}>
            Next
          </button>
        </div>
      </div>

      {/* DC Modal */}
      {showDCModal && (
        <div className="lg-modal-overlay">
          <div className="lg-modal-card lg-modal-sm">
            <h3 style={{ marginTop: 0, marginBottom: '16px' }}>Generate Delivery Challan ({selectedIds.size > 0 ? selectedIds.size : 1} order{(selectedIds.size > 1) ? 's' : ''})</h3>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>From: <strong>LOGO GEAR SOLUTION LLP</strong></p>
            <div className="lg-form-grid">
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600 }}>Warehouse (From Address) *</label>
                <select value={dcForm.warehouse_id} onChange={e => setDcForm({ ...dcForm, warehouse_id: Number(e.target.value) })}
                  className="logistics-filter-select" style={{ width: '100%', padding: '8px 10px' }}>
                  <option value={0}>-- Select Warehouse --</option>
                  {warehouses.map((wh: any) => (
                    <option key={wh.id} value={wh.id}>
                      {wh.name}{wh.is_default ? ' (Default)' : ''} — {wh.city || ''}{wh.state ? `, ${wh.state}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="lg-grid-2">
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600 }}>Vehicle Number</label>
                  <input value={dcForm.vehicle_number} onChange={e => setDcForm({ ...dcForm, vehicle_number: e.target.value })}
                    className="logistics-filter-input" style={{ width: '100%' }} />
                </div>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600 }}>Transport Mode</label>
                  <select value={dcForm.transport_mode} onChange={e => setDcForm({ ...dcForm, transport_mode: e.target.value })}
                    className="logistics-filter-select" style={{ width: '100%' }}>
                    <option value="courier">Courier</option>
                    <option value="road">Road</option>
                    <option value="hand_delivery">Hand Delivery</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="lg-modal-actions">
              <button className="logistics-btn logistics-btn-cancel" onClick={() => setShowDCModal(false)}>Cancel</button>
              <button className="logistics-btn logistics-btn-primary" onClick={handleGenerateDC} disabled={processing || !dcForm.warehouse_id}>
                {processing ? 'Generating...' : 'Generate DC'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AWB Generation Modal */}
      {showAwbModal && (
        <div className="lg-modal-overlay">
          <div className="lg-modal-card">
            <h3 style={{ marginTop: 0, marginBottom: '4px' }}>Generate AWB</h3>
            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '16px' }}>
              {awbOrderIds.length === 1 ? '1 order' : `${awbOrderIds.length} orders`} selected
            </p>

            {awbChannelDefaults && (
              <div style={{ padding: '10px 14px', borderRadius: '6px', background: '#f0fdf4', border: '1px solid #86efac', fontSize: '12px', color: '#166534', marginBottom: '14px' }}>
                Channel defaults applied from: <strong>{awbChannelDefaults.name}</strong>
              </div>
            )}

            {/* Carrier & Service */}
            <div className="lg-grid-2" style={{ marginBottom: '14px' }}>
              <div>
                <label style={{ fontSize: '11px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Carrier</label>
                <select value={awbForm.carrier} onChange={e => setAwbForm({ ...awbForm, carrier: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}>
                  <option value="delhivery">Delhivery</option>
                  <option value="bluedart">BlueDart</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: '11px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Service</label>
                {awbForm.carrier === 'delhivery' ? (
                  <select value={awbForm.shipping_mode} onChange={e => setAwbForm({ ...awbForm, shipping_mode: e.target.value })}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}>
                    <option value="Surface">Surface</option>
                    <option value="Express">Express</option>
                  </select>
                ) : (
                  <select disabled style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', background: '#f8fafc' }}>
                    <option>Express</option>
                  </select>
                )}
              </div>
            </div>

            {/* Package picker */}
            <div style={{ marginBottom: '14px' }}>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Select Package (optional)</label>
              <select value={awbForm.package_id} onChange={e => handlePackageSelect(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}>
                <option value="">-- Manual / Use values below --</option>
                {awbPackages.map((pkg: any) => (
                  <option key={pkg.id} value={pkg.id}>
                    {pkg.name} ({pkg.length}x{pkg.width}x{pkg.height} cm, {pkg.weight}kg)
                  </option>
                ))}
              </select>
            </div>

            {/* Dimensions */}
            <div className="lg-grid-4" style={{ marginBottom: '14px' }}>
              <div>
                <label style={{ fontSize: '11px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Length (cm)</label>
                <input type="number" value={awbForm.length} onChange={e => setAwbForm({ ...awbForm, length: e.target.value, package_id: '' })}
                  style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
              </div>
              <div>
                <label style={{ fontSize: '11px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Width (cm)</label>
                <input type="number" value={awbForm.width} onChange={e => setAwbForm({ ...awbForm, width: e.target.value, package_id: '' })}
                  style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
              </div>
              <div>
                <label style={{ fontSize: '11px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Height (cm)</label>
                <input type="number" value={awbForm.height} onChange={e => setAwbForm({ ...awbForm, height: e.target.value, package_id: '' })}
                  style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
              </div>
              <div>
                <label style={{ fontSize: '11px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Weight (kg)</label>
                <input type="number" step="0.1" value={awbForm.weight} onChange={e => setAwbForm({ ...awbForm, weight: e.target.value, package_id: '' })}
                  style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
              </div>
            </div>

            {/* Declared value */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '11px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Declared Value (₹) — optional, defaults to order total</label>
              <input type="number" value={awbForm.declared_value} onChange={e => setAwbForm({ ...awbForm, declared_value: e.target.value })}
                placeholder="Leave empty to use order total"
                style={{ width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
            </div>

            {/* Volumetric weight info */}
            {awbForm.length && awbForm.width && awbForm.height && (
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '16px' }}>
                Volumetric weight: {((parseFloat(awbForm.length) * parseFloat(awbForm.width) * parseFloat(awbForm.height)) / 5000).toFixed(2)} kg
              </div>
            )}

            {/* Actions */}
            <div className="lg-modal-actions">
              <button className="logistics-btn logistics-btn-cancel" onClick={() => setShowAwbModal(false)}>Cancel</button>
              <button className="logistics-btn logistics-btn-secondary" onClick={() => { setAwbForm(prev => ({ weight: '2', length: '10', width: '10', height: '10', declared_value: '2500', package_id: '', carrier: prev.carrier, shipping_mode: prev.shipping_mode })); handleAwbSubmit(); }}>
                Skip (Use Defaults)
              </button>
              <button className="logistics-btn logistics-btn-primary" onClick={handleAwbSubmit}>
                Generate AWB
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Order Detail Panel */}
      {selectedOrder && (
        <div className="lg-detail-panel">
          {/* Panel Header */}
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '16px' }}>{selectedOrder.order_number}</h3>
              {selectedOrder.external_order_id && <div style={{ fontSize: '12px', color: '#64748b' }}>Ext: {selectedOrder.external_order_id}</div>}
            </div>
            <button onClick={() => setSelectedOrder(null)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#64748b' }}>✕</button>
          </div>

          {/* Panel Body */}
          <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
            {/* Order Info */}
            <div className="lg-detail-info-grid">
              <div><label style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Customer</label><div style={{ fontSize: '14px', fontWeight: 600 }}>{selectedOrder.customer_name || '-'}</div></div>
              <div><label style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Channel</label><div style={{ fontSize: '14px' }}>{selectedOrder.channel || '-'}</div></div>
              <div><label style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Order Date</label><div style={{ fontSize: '14px' }}>{formatDate(selectedOrder.order_date)}</div></div>
              <div><label style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Status</label><div>{getStatusBadge(selectedOrder.status)}</div></div>
              <div><label style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Amount</label><div style={{ fontSize: '14px', fontWeight: 600 }}>₹{selectedOrder.total_amount.toFixed(2)}</div></div>
            </div>

            {/* DC Section */}
            <div style={{ padding: '16px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #86efac', marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#166534' }}>Delivery Challan</h4>
              {selectedOrder.dc_number ? (
                <div>
                  <div style={{ fontSize: '13px', marginBottom: '8px' }}>
                    <strong>DC:</strong> {selectedOrder.dc_number} <span style={{ color: '#64748b' }}>({formatDate(selectedOrder.dc_date)})</span>
                  </div>
                  <button className="logistics-btn logistics-btn-primary logistics-btn-sm" onClick={() => selectedOrder.dc_id && viewPDF(selectedOrder.dc_id)}>
                    📄 View / Download PDF
                  </button>
                </div>
              ) : (
                <div>
                  <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 10px 0' }}>No DC generated yet.</p>
                  <button className="logistics-btn logistics-btn-primary logistics-btn-sm" onClick={() => { setSelectedIds(new Set([selectedOrder.id])); openDCModal(); }}>
                    Generate DC
                  </button>
                </div>
              )}
            </div>

            {/* AWB Section */}
            <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#475569' }}>Shipment / AWB</h4>
              {/* Serviceability quick-check — only show if not yet shipped */}
              {!selectedOrder.awb_number && (
              <div style={{ marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #e2e8f0' }}>
                <button className="logistics-btn logistics-btn-secondary logistics-btn-sm"
                  onClick={() => handleSingleServiceabilityCheck(selectedOrder.id)}
                  disabled={serviceabilityChecking}
                  style={{ fontSize: '12px' }}>
                  {serviceabilityChecking ? 'Checking...' : '📍 Check Serviceability'}
                </button>
              </div>
              )}
              {selectedOrder.awb_number ? (
                <div>
                  <div style={{ fontSize: '13px', marginBottom: '8px' }}>
                    <strong>AWB:</strong> <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{selectedOrder.awb_number}</span>
                  </div>
                  {selectedOrder.shipping_provider && (
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>
                      Provider: {selectedOrder.shipping_provider}
                    </div>
                  )}
                  {selectedOrder.ship_date && (
                    <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>
                      Booked: {formatDate(selectedOrder.ship_date)}
                    </div>
                  )}
                  {selectedOrder.shipping_status && (
                    <div style={{ fontSize: '12px', marginBottom: '12px' }}>
                      <span style={{ display: 'inline-block', padding: '3px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
                        background: selectedOrder.shipping_status === 'booked' ? '#fff3cd' : selectedOrder.shipping_status === 'in_transit' ? '#cce5ff' : selectedOrder.shipping_status === 'delivered' ? '#d4edda' : '#f1f5f9',
                        color: selectedOrder.shipping_status === 'booked' ? '#856404' : selectedOrder.shipping_status === 'in_transit' ? '#004085' : selectedOrder.shipping_status === 'delivered' ? '#155724' : '#383d41'
                      }}>
                        {selectedOrder.shipping_status === 'booked' && 'Pickup Scheduled'}
                        {selectedOrder.shipping_status === 'picked_up' && 'Picked Up'}
                        {selectedOrder.shipping_status === 'in_transit' && 'In Transit'}
                        {selectedOrder.shipping_status === 'out_for_delivery' && 'Out for Delivery'}
                        {selectedOrder.shipping_status === 'delivered' && 'Delivered'}
                        {!['booked', 'picked_up', 'in_transit', 'out_for_delivery', 'delivered'].includes(selectedOrder.shipping_status) && selectedOrder.shipping_status}
                      </span>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <button className="logistics-btn logistics-btn-primary logistics-btn-sm" onClick={() => handleTrackOrderAWB(selectedOrder.id)} disabled={tracking}>
                      {tracking ? '...' : '📍 Track'}
                    </button>
                    <button className="logistics-btn logistics-btn-primary logistics-btn-sm" onClick={() => printAWB(selectedOrder.id, 'thermal')}>
                      🖨 Print Label
                    </button>
                    {selectedOrder.shipping_provider?.toLowerCase() === 'bluedart' && (
                      <button className="logistics-btn logistics-btn-secondary logistics-btn-sm" onClick={() => downloadAWB(selectedOrder.id, 'a4')}>
                        📄 A4 Label
                      </button>
                    )}
                    <button className="logistics-btn logistics-btn-secondary logistics-btn-sm" onClick={() => downloadAWB(selectedOrder.id, 'thermal')}>
                      ⬇ Download
                    </button>
                    <button className="logistics-btn logistics-btn-cancel logistics-btn-sm"
                      onClick={() => handleCancelAWBClick(selectedOrder.id, selectedOrder.awb_number || `Order #${selectedOrder.order_number}`)}
                      disabled={['picked_up', 'in_transit', 'out_for_delivery', 'delivered'].includes(selectedOrder.shipping_status || '')}
                      title={['picked_up', 'in_transit', 'out_for_delivery', 'delivered'].includes(selectedOrder.shipping_status || '') ? 'Cannot cancel — shipment already in transit' : 'Cancel this AWB'}>
                      ✕ Cancel AWB
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 10px 0' }}>No AWB generated yet.</p>
                  {selectedOrder.dc_number ? (
                    <button className="logistics-btn logistics-btn-primary logistics-btn-sm"
                      onClick={() => handleGenerateAWB(selectedOrder.id)} disabled={processing}>
                      {processing ? 'Generating...' : 'Generate AWB'}
                    </button>
                  ) : (
                    <p style={{ fontSize: '12px', color: '#f59e0b', margin: 0 }}>Generate DC first to enable AWB generation.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Backdrop for detail panel */}
      {selectedOrder && (
        <div onClick={() => setSelectedOrder(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.2)', zIndex: 998 }} />
      )}

      <ConfirmDeleteModal
        isOpen={cancelConfirm.show}
        title="Cancel AWB"
        message="Are you sure you want to cancel this AWB? This action cannot be undone."
        itemName={cancelConfirm.name}
        confirmLabel="Cancel AWB"
        onConfirm={handleCancelAWB as any}
        onCancel={() => setCancelConfirm({show: false, id: null, name: ''})}
      />

      {/* Schedule Pickup Modal */}
      {showPickupModal && (
        <div className="lg-modal-overlay">
          <div className="lg-modal-card lg-modal-xs">
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '16px' }}>Schedule Pickup</h3>

            {pendingPickupCount && (
              <div style={{ padding: '10px 14px', background: '#f0fdf4', borderRadius: '8px', marginBottom: '16px', fontSize: '13px' }}>
                <strong>Pending packages:</strong> Delhivery: {pendingPickupCount.delhivery || 0}, BlueDart: {pendingPickupCount.bluedart || 0}
              </div>
            )}

            <div style={{ display: 'grid', gap: '12px', marginBottom: '20px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Carrier</label>
                <select value={pickupForm.carrier} onChange={(e) => setPickupForm({ ...pickupForm, carrier: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }}>
                  <option value="delhivery">Delhivery</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Pickup Date</label>
                <input type="date" value={pickupForm.date} onChange={(e) => setPickupForm({ ...pickupForm, date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155', display: 'block', marginBottom: '4px' }}>Pickup Time</label>
                <input type="time" value={pickupForm.time} onChange={(e) => setPickupForm({ ...pickupForm, time: e.target.value })}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px' }} />
              </div>
            </div>

            {pickupResult && (
              <div style={{ padding: '10px 14px', borderRadius: '8px', marginBottom: '16px', fontSize: '13px', background: pickupResult.success ? '#d1fae5' : '#fef2f2', color: pickupResult.success ? '#065f46' : '#991b1b' }}>
                {pickupResult.success ? pickupResult.message : (pickupResult.error || 'Scheduling failed')}
                {pickupResult.pending_awbs && pickupResult.pending_awbs.length > 0 && (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#334155' }}>
                    <strong>Packages included ({pickupResult.pending_awbs.length}):</strong>
                    <div style={{ marginTop: '4px', maxHeight: '100px', overflow: 'auto' }}>
                      {pickupResult.pending_awbs.map((p: any, i: number) => (
                        <div key={i} style={{ padding: '2px 0' }}>{p.order_number} — <span style={{ fontFamily: 'monospace' }}>{p.awb}</span></div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="lg-modal-actions" style={{ alignItems: 'center' }}>
              <a href="https://one.delhivery.com" target="_blank" rel="noopener noreferrer"
                style={{ fontSize: '12px', color: '#0d9488', textDecoration: 'underline', marginRight: 'auto' }}>
                Manage Pickups on Delhivery One
              </a>
              <button className="logistics-btn logistics-btn-secondary" onClick={() => setShowPickupModal(false)}>Close</button>
              <button className="logistics-btn logistics-btn-primary" onClick={handleSchedulePickup} disabled={pickupScheduling || !pickupForm.date}>
                {pickupScheduling ? 'Scheduling...' : 'Schedule Pickup'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LogisticsDashboard;
